/**
 * PATH: apps/qa/src/pages/new-inspection/DetailsStep.tsx
 * PURPOSE: Step 1 — inspection details form with AI badges (category, project type, building class, trades)
 */

import { Check, Plus, Sparkles, AlertCircle, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  BUILDING_CLASS_INFO, CATEGORY_INFO, PROJECT_TYPE_INFO,
  TRADE_GROUPS, TRADE_INFO,
} from '@/lib/inspection-constants'
import { Tooltip, JobContextCard, FieldLabel } from '@/components/inspection'
import type { Job } from './inspection-types'

interface DetailsStepProps {
  selectedJob: Job | null
  inspectionName: string; setInspectionName: (v: string) => void
  category: 'residential' | 'commercial' | 'infrastructure'; setCategory: (v: 'residential' | 'commercial' | 'infrastructure') => void
  projectType: 'new_construction' | 'renovation' | 'remediation'; setProjectType: (v: 'new_construction' | 'renovation' | 'remediation') => void
  buildingClasses: string[]; setBuildingClasses: (v: string[]) => void
  tradeTags: string[]; suggestedClasses: string[]; suggestedTrades: string[]
  customTradeTags: string[]; newCustomTag: string; setNewCustomTag: (v: string) => void
  customTagInputRef: React.RefObject<HTMLInputElement | null>
  aiSuggestedCategory: string | null; aiSuggestedClass: string | null; aiSuggestedTrades: string[]
  aiConfidence: number; aiReasoning: string
  setHasManualClassSelection: (v: boolean) => void
  toggleTradeTag: (tag: string) => void; addCustomTag: () => void; removeCustomTag: (tag: string) => void
}

export default function DetailsStep(props: DetailsStepProps) {
  const {
    selectedJob, inspectionName, setInspectionName,
    category, setCategory, projectType, setProjectType,
    buildingClasses, setBuildingClasses, tradeTags, suggestedClasses, suggestedTrades,
    customTradeTags, newCustomTag, setNewCustomTag, customTagInputRef,
    aiSuggestedCategory, aiSuggestedClass, aiSuggestedTrades,
    aiConfidence, aiReasoning, setHasManualClassSelection,
    toggleTradeTag, addCustomTag, removeCustomTag,
  } = props

  return (
    <div className="space-y-6">
      {/* AI Confidence Banner */}
      {aiConfidence > 0 && (
        <div className={cn(
          'flex items-center gap-3 px-4 py-3 rounded-xl border',
          aiConfidence >= 0.7
            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-300'
            : aiConfidence >= 0.5
              ? 'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-300'
              : 'bg-muted border-border text-muted-foreground'
        )}>
          <Sparkles className="w-5 h-5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium">AI pre-selected: {aiReasoning}</p>
            <p className="text-xs opacity-75">{Math.round(aiConfidence * 100)}% confident • Items marked with ✨ were suggested by AI</p>
          </div>
        </div>
      )}

      <JobContextCard job={selectedJob} />

      <div>
        <h2 className="text-lg font-semibold text-foreground mb-2">Inspection Details</h2>
        <p className="text-muted-foreground text-sm">Configure the inspection type and requirements. AI has pre-selected based on job data.</p>
      </div>

      {/* Category Selection */}
      <div>
        <FieldLabel tooltip="Determines which building classes are suggested">Category</FieldLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(Object.entries(CATEGORY_INFO) as [keyof typeof CATEGORY_INFO, typeof CATEGORY_INFO[keyof typeof CATEGORY_INFO]][]).map(([key, info]) => {
            const Icon = info.icon; const isSelected = category === key; const isAiSuggested = aiSuggestedCategory === key
            return (
              <button key={key} onClick={() => setCategory(key as typeof category)}
                className={cn('p-4 rounded-xl border-2 text-left transition-all relative', isSelected ? 'border-primary bg-primary/5 shadow-sm' : 'border-border hover:border-primary/50 hover:bg-accent')}>
                {isAiSuggested && <span className="absolute -top-2 -right-2 bg-amber-500 text-white text-[10px] font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5 shadow-sm"><Sparkles className="w-2.5 h-2.5" />AI</span>}
                <div className={cn('w-10 h-10 rounded-lg flex items-center justify-center mb-2 transition-colors', isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}><Icon className="w-5 h-5" /></div>
                <p className={cn('font-medium transition-colors', isSelected ? 'text-primary' : 'text-foreground')}>{info.name}</p>
                <p className="text-xs text-muted-foreground mt-1">{info.description}</p>
              </button>
            )
          })}
        </div>
      </div>

      {/* Project Type */}
      <div>
        <FieldLabel tooltip="The type of construction work being inspected">Project Type</FieldLabel>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {(Object.entries(PROJECT_TYPE_INFO) as [keyof typeof PROJECT_TYPE_INFO, typeof PROJECT_TYPE_INFO[keyof typeof PROJECT_TYPE_INFO]][]).map(([key, info]) => {
            const Icon = info.icon; const isSelected = projectType === key
            return (
              <button key={key} onClick={() => setProjectType(key as typeof projectType)}
                className={cn('p-3 rounded-xl border-2 text-left transition-all flex items-center gap-3', isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-accent')}>
                <div className={cn('w-8 h-8 rounded-lg flex items-center justify-center transition-colors', isSelected ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground')}><Icon className="w-4 h-4" /></div>
                <div>
                  <p className={cn('font-medium text-sm transition-colors', isSelected ? 'text-primary' : 'text-foreground')}>{info.name}</p>
                  <p className="text-xs text-muted-foreground">{info.description}</p>
                </div>
              </button>
            )
          })}
        </div>
      </div>

      {/* Building Classes */}
      <BuildingClassSelector
        buildingClasses={buildingClasses} setBuildingClasses={setBuildingClasses}
        aiSuggestedClass={aiSuggestedClass} suggestedClasses={suggestedClasses}
        setHasManualClassSelection={setHasManualClassSelection}
      />

      {/* Trade Tags */}
      <TradeTagsSelector
        tradeTags={tradeTags} suggestedTrades={suggestedTrades} aiSuggestedTrades={aiSuggestedTrades}
        category={category} customTradeTags={customTradeTags}
        newCustomTag={newCustomTag} setNewCustomTag={setNewCustomTag}
        customTagInputRef={customTagInputRef}
        toggleTradeTag={toggleTradeTag} addCustomTag={addCustomTag} removeCustomTag={removeCustomTag}
        onSelectAllSuggested={(trades) => trades}
        setTradeTags={() => {}}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────
// Sub-components (kept in same file since they're tightly coupled to step)
// ─────────────────────────────────────────────────────────────────────────────

function BuildingClassSelector({ buildingClasses, setBuildingClasses, aiSuggestedClass, suggestedClasses, setHasManualClassSelection }: {
  buildingClasses: string[]; setBuildingClasses: (v: string[]) => void
  aiSuggestedClass: string | null; suggestedClasses: string[]
  setHasManualClassSelection: (v: boolean) => void
}) {
  return (
    <div>
      <FieldLabel tooltip="Australian NCC building classifications for compliance. Usually only one class applies.">Building Class (NCC)</FieldLabel>
      <p className="text-xs text-muted-foreground mb-3">Select the primary building class for this inspection. Most projects require only one class.</p>
      <div className="flex flex-wrap gap-2">
        {Object.entries(BUILDING_CLASS_INFO).map(([cls, info]) => {
          const Icon = info.icon; const isSelected = buildingClasses.includes(cls)
          const isAiSuggested = aiSuggestedClass === cls; const isSuggestedForCategory = suggestedClasses.includes(cls)
          return (
            <Tooltip key={cls} content={
              <div className="w-64">
                <div className="flex items-center gap-2 mb-2"><Icon className="w-4 h-4 text-primary" /><span className="font-semibold">{info.name}</span>{isAiSuggested && <span className="text-[10px] bg-amber-500 text-white px-1 py-0.5 rounded">✨ AI</span>}</div>
                <p className="text-xs mb-2">{info.description}</p>
                <p className="text-xs text-muted-foreground"><strong>Examples:</strong> {info.examples}</p>
              </div>
            }>
              <button onClick={() => { setBuildingClasses([cls]); setHasManualClassSelection(true) }}
                className={cn(
                  'px-3 py-1.5 rounded-lg text-sm font-medium transition-all inline-flex items-center gap-1.5 relative',
                  'focus:outline-none focus:ring-2 focus:ring-primary/50 active:scale-95',
                  isSelected ? 'bg-primary text-primary-foreground shadow-md ring-2 ring-primary ring-offset-1 ring-offset-background'
                    : isAiSuggested ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-2 border-amber-500/50 hover:bg-amber-500/25'
                    : isSuggestedForCategory ? 'bg-muted text-foreground border border-primary/30 hover:border-primary/50 hover:bg-accent'
                    : 'bg-muted/60 text-muted-foreground border border-border hover:bg-muted hover:text-foreground'
                )}>
                {isAiSuggested && !isSelected && <span className="absolute -top-1.5 -right-1.5 bg-amber-500 text-white text-[8px] font-bold px-1 py-0.5 rounded-full flex items-center">✨</span>}
                <Icon className="w-3.5 h-3.5" />{cls}{isSelected && <Check className="w-3 h-3 animate-in zoom-in duration-150" />}
              </button>
            </Tooltip>
          )
        })}
      </div>
      {buildingClasses.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Select at least one building class for compliance</p>
      )}
    </div>
  )
}

function TradeTagsSelector({ tradeTags, suggestedTrades, aiSuggestedTrades, category, customTradeTags, newCustomTag, setNewCustomTag, customTagInputRef, toggleTradeTag, addCustomTag, removeCustomTag }: {
  tradeTags: string[]; suggestedTrades: string[]; aiSuggestedTrades: string[]
  category: string; customTradeTags: string[]
  newCustomTag: string; setNewCustomTag: (v: string) => void
  customTagInputRef: React.RefObject<HTMLInputElement | null>
  toggleTradeTag: (tag: string) => void; addCustomTag: () => void; removeCustomTag: (tag: string) => void
  onSelectAllSuggested: (t: string[]) => string[]; setTradeTags: (fn: any) => void
}) {
  return (
    <div>
      <FieldLabel tooltip="The trades/disciplines that will be inspected">Trade Tags</FieldLabel>
      {/* Custom Tags Input */}
      <div className="mb-4 p-3 rounded-lg bg-muted/30 border border-dashed border-border">
        <div className="flex items-center gap-2 mb-2"><Plus className="w-4 h-4 text-muted-foreground" /><span className="text-xs font-medium text-muted-foreground">Add Custom Trade</span></div>
        <div className="flex gap-2">
          <input ref={customTagInputRef} type="text" value={newCustomTag} onChange={(e) => setNewCustomTag(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') addCustomTag(); if (e.key === 'Escape') { setNewCustomTag(''); customTagInputRef.current?.blur() } }}
            placeholder="Type trade name and press Enter..."
            className="flex-1 px-3 py-1.5 rounded-md border border-input bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500 transition-all" />
          <button onClick={addCustomTag} disabled={!newCustomTag.trim()} className="px-3 py-1.5 rounded-md bg-purple-500 text-white text-sm font-medium hover:bg-purple-600 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all">Add</button>
        </div>
        {customTradeTags.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mt-2">
            {customTradeTags.map(tag => (
              <span key={tag} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-purple-500 text-white text-xs font-medium">
                {tag}<button onClick={() => removeCustomTag(tag)} className="hover:bg-white/20 rounded p-0.5 transition-colors"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
        )}
      </div>

      {/* AI/Suggested Trades */}
      {suggestedTrades.length > 0 && (
        <div className="mb-4">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-medium text-purple-700 dark:text-purple-400 flex items-center gap-1"><Sparkles className="w-3 h-3" />Recommended for {CATEGORY_INFO[category as keyof typeof CATEGORY_INFO]?.name}</p>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {suggestedTrades.map(tag => {
              const info = TRADE_INFO[tag]; if (!info) return null
              const Icon = info.icon; const isSelected = tradeTags.includes(tag)
              return (
                <button key={tag} onClick={() => toggleTradeTag(tag)}
                  className={cn('px-2.5 py-1.5 rounded-md text-xs font-medium transition-all inline-flex items-center gap-1', 'focus:outline-none focus:ring-2 focus:ring-purple-500/50 active:scale-95',
                    isSelected ? 'bg-purple-600 text-white shadow-md ring-2 ring-purple-600 ring-offset-1 ring-offset-background' : 'bg-purple-500/90 text-white hover:bg-purple-600 shadow-sm')}>
                  <Icon className="w-3.5 h-3.5" />{info.name}{isSelected && <Check className="w-3 h-3" />}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* All Trades by Group */}
      <div className="space-y-3">
        {Object.entries(TRADE_GROUPS).map(([groupName, group]) => {
          const selectedInGroup = group.trades.filter(t => tradeTags.includes(t)).length
          return (
            <div key={groupName}>
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">{groupName}</span>
                {selectedInGroup > 0 && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-500/10 text-purple-600 dark:text-purple-400">{selectedInGroup}/{group.trades.length}</span>}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {group.trades.map(tag => {
                  const info = TRADE_INFO[tag]; if (!info) return null
                  const Icon = info.icon; const isSelected = tradeTags.includes(tag)
                  const isSuggested = suggestedTrades.includes(tag); const isAiSuggested = aiSuggestedTrades.includes(tag)
                  return (
                    <button key={tag} onClick={() => toggleTradeTag(tag)}
                      className={cn('px-2 py-1 rounded-md text-xs font-medium transition-all inline-flex items-center gap-1 relative', 'focus:outline-none focus:ring-2 focus:ring-purple-500/50 active:scale-95',
                        isSelected ? 'bg-purple-500 text-white shadow-sm'
                          : isAiSuggested ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border border-amber-500/50 hover:bg-amber-500/25'
                          : isSuggested ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/50'
                          : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground')}>
                      {isAiSuggested && !isSelected && <span className="absolute -top-1 -right-1 text-[8px]">✨</span>}
                      <Icon className={cn('w-3 h-3', !isSelected && info.color)} />{info.name}{isSelected && <Check className="w-2.5 h-2.5" />}
                    </button>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>

      {tradeTags.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-3 flex items-center gap-1"><AlertCircle className="w-3 h-3" />Select at least one trade for inspection tracking</p>
      )}
    </div>
  )
}
